import React, { useMemo, Fragment } from 'react';
import {
    HeadingText20,
    IconButton,
    Icons,
    HexOrNamedColor,
    pxToRem,
    CalloutInline,
    BodyTextMedium,
    Icon,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { filterNullish } from '@modernhealth/filter-nullish';
import styled from '@emotion/styled';
import { useRouteMatch, Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    InternalLinkNode,
    PathwayProgressState,
    PathwaySessionState,
} from 'src/generated/webapp_gql';
import { SidebarLayout } from 'src/shared/layout/SidebarLayout';
import { useQueryParams } from 'src/shared/hooks/useQueryParams';
import { TopicListItem } from 'src/modules/pathways/components/TopicListItem';
import { SessionDetailBanner } from 'src/modules/pathways/components/SessionDetailBanner';
import { PathwayDetailHeader } from 'src/modules/pathways/components/PathwayDetailHeader';
import { RecommendedActivities } from 'src/modules/pathways/components/RecommendedActivities';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';
import { ProviderAction } from 'src/modules/care/components/ProviderAction';

import type { PathwayDetailProps } from '../PathwayPage';

const TopicStateToIcon: Record<PathwaySessionState, Icons> = {
    [PathwaySessionState.Unscheduled]: 'star',
    [PathwaySessionState.Scheduled]: 'star',
    [PathwaySessionState.Joinable]: 'star',
    [PathwaySessionState.Completed]: 'checkmark',
    [PathwaySessionState.Locked]: 'lock',
};

const TopicStateToColor: Record<PathwaySessionState, HexOrNamedColor> = {
    [PathwaySessionState.Unscheduled]: 'blue55',
    [PathwaySessionState.Scheduled]: 'blue55',
    [PathwaySessionState.Joinable]: 'blue55',
    [PathwaySessionState.Completed]: 'green40',
    [PathwaySessionState.Locked]: 'gray30',
};

export const PathwayInProgress = ({ pathway, memberPathway }: PathwayDetailProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const route = useRouteMatch();
    const queryParams = useQueryParams();
    const pathwayTopics = useMemo(
        () => filterNullish(memberPathway.pathwayTopics ?? []),
        [memberPathway.pathwayTopics],
    );

    const showCallout = useMemo(() => {
        return (
            memberPathway.state === PathwayProgressState.InProgress &&
            memberPathway.pathwayTopics.every(
                topic => topic?.sessionDetail?.state === PathwaySessionState.Completed,
            )
        );
    }, [memberPathway.pathwayTopics, memberPathway.state]);

    const filteredCareActions = useMemo(() => {
        return filterNullish(pathway.careActions ?? []).filter(
            (p): p is InternalLinkNode => p?.__typename === 'InternalLinkNode',
        );
    }, [pathway.careActions]);

    const activeSessionQueryParamString = queryParams.get('activeSession');
    const activeSessionQueryParamNumber = activeSessionQueryParamString
        ? parseInt(activeSessionQueryParamString, 10)
        : NaN;
    const activeSessionQueryParam = !Number.isNaN(activeSessionQueryParamNumber)
        ? activeSessionQueryParamNumber - 1
        : -1;

    if (
        activeSessionQueryParamString &&
        (activeSessionQueryParam < 0 || activeSessionQueryParam >= pathwayTopics.length)
    ) {
        return <Redirect to={route.url} />;
    }

    const activeSessionIndex = activeSessionQueryParamString
        ? activeSessionQueryParam
        : memberPathway.currentSessionIndex;
    const activeSessionDisplayableNumber = activeSessionIndex + 1;
    const activeTopic = pathwayTopics[activeSessionIndex];
    const isActiveTopicLocked = activeTopic.sessionDetail?.state === PathwaySessionState.Locked;

    return (
        <PageContentContainer>
            <StyledBackButton
                title={t('common.back')}
                size="large"
                icon="arrow-left"
                onClick={history.goBack}
            />
            <PathwayDetailHeader
                title={pathway.title}
                pathwayProvider={memberPathway.pathwayProvider}
                currentSessionIndex={memberPathway.currentSessionIndex}
                progressPercentage={memberPathway.completionPercent}
                totalSessions={pathwayTopics.length}
            />
            <SidebarLayout.Container>
                <SidebarLayout.Sidebar backgroundColor="transparent">
                    {pathwayTopics.map((t, i) => {
                        const active = i === activeSessionIndex;
                        const icon: Icons = t.sessionDetail
                            ? TopicStateToIcon[t.sessionDetail.state]
                            : 'star';
                        const color: HexOrNamedColor = t.sessionDetail
                            ? TopicStateToColor[t.sessionDetail.state]
                            : 'blue55';
                        const notLastTopic = i < pathwayTopics.length - 1;

                        return (
                            <Fragment key={t.title}>
                                <TopicListItem
                                    title={t.title}
                                    subtitle={t.subtitle}
                                    active={active}
                                    to={{
                                        pathname: route.url,
                                        search: `activeSession=${i + 1}`,
                                    }}
                                    color={color}
                                    icon={icon}
                                />
                                {notLastTopic && <StyledSeparator />}
                            </Fragment>
                        );
                    })}
                </SidebarLayout.Sidebar>
                <SidebarLayout.Content>
                    {isActiveTopicLocked && (
                        <PathwayLockedContainer>
                            <StyledIconWrapper>
                                <Icon name="lock" color="white" size={14} />
                            </StyledIconWrapper>
                            <HeadingText20 marginTop={12} centered>
                                {t('pathways:pathway_detail.topic_locked')}
                            </HeadingText20>
                        </PathwayLockedContainer>
                    )}

                    <ContentContainer>
                        {showCallout && (
                            <CalloutContainer>
                                <CalloutInline variant="info" showIcon={false}>
                                    <HeadingText20 bold marginBottom={8} fontColor="violet85">
                                        {t('pathways:pathway_detail.next_steps.title', {
                                            provider:
                                                memberPathway.pathwayProvider.provider.firstName,
                                        })}
                                    </HeadingText20>
                                    <BodyTextMedium fontColor="violet85">
                                        {t('pathways:pathway_detail.next_steps.description', {
                                            provider:
                                                memberPathway.pathwayProvider.provider.firstName,
                                        })}
                                    </BodyTextMedium>
                                </CalloutInline>
                            </CalloutContainer>
                        )}

                        <SessionDetailBanner
                            topic={activeTopic}
                            pathwayProvider={memberPathway.pathwayProvider}
                            pathwayProgress={memberPathway.state}
                        />
                        {activeTopic.recommendedActivities || activeTopic.assessmentActivity ? (
                            <RecommendedActivities
                                pathwayUuid={pathway.uuid}
                                pathwayProvider={memberPathway.pathwayProvider}
                                pathwayProgress={memberPathway.state}
                                topicHeader={activeTopic.title}
                                title={t('pathways:pathway_detail.recommended_activities.title', {
                                    sessionNumber: activeSessionDisplayableNumber,
                                })}
                                description={t(
                                    'pathways:pathway_detail.recommended_activities.description',
                                )}
                                activities={activeTopic.recommendedActivities}
                                assessmentActivity={activeTopic.assessmentActivity}
                            />
                        ) : null}
                        {filteredCareActions.length > 0 && (
                            <CareActionsWrapper>
                                {filteredCareActions.map((action, index) => (
                                    <CareActionItemWrapper key={action?.uuid}>
                                        <ProviderAction action={action} elementIndex={index} />
                                    </CareActionItemWrapper>
                                ))}
                            </CareActionsWrapper>
                        )}

                        {isActiveTopicLocked && <LinearGradient />}
                    </ContentContainer>
                </SidebarLayout.Content>
            </SidebarLayout.Container>
        </PageContentContainer>
    );
};

const CalloutContainer = styled.div({
    marginBottom: '24px',
});

const StyledSeparator = styled.div({
    width: 0,
    height: 20,
    borderLeft: `1px dashed ${colors.gray40}`,
    marginTop: 10,
    marginBottom: 10,
    alignSelf: 'center',
});

const StyledBackButton = styled(IconButton)({
    border: '0 !important', // sorry design system
    margin: '25px 0',
    transform: 'translateX(-15px)',
});

const CareActionsWrapper = styled.div({
    border: `2px solid ${colors.gray05}`,
    borderRadius: pxToRem(8),
    marginTop: pxToRem(28),
});

const CareActionItemWrapper = styled.div({
    padding: pxToRem(24),
});

const ContentContainer = styled.div({
    position: 'relative',
});

const LinearGradient = styled.div({
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, #FFFFFF 82.29%)',
});

const PathwayLockedContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '32px 20px',
    gap: '10px',
    background: colors.violet05,
    marginBottom: '24px',
});

const StyledIconWrapper = styled.span({
    backgroundColor: colors.violet55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: '6px',
});
